import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';

import {
  useCustomAssessmentQuery,
  usePatientsV2NamesQuery,
  useCompleteProviderOnlyFormMutation,
  PatientLifecycleStatusV2,
  SignatureUser,
  Patient,
  PatientName,
} from '../../../generated/graphql';

import {
  isProviderSignatureAssessmentQuestion,
  isStatementAssessmentQuestion,
} from '../../lib/assessments';
import {
  mapAssessmentAnswersToMutationInput,
  ProgramAssessmentQuestion,
} from '../../lib/program-assessment';
import { getTimeStamp } from '../../lib/time';

import { useAuth } from '../../../contexts/AuthContext';
import { isExpiredTokenError } from '../../../lib/errors';
import { logger } from '../../../lib/logger';
import { clearTokenPayload, clearAuth0AccessToken } from '../../../lib/auth';
import Auth0Helpers from '../../../lib/auth0';

import Spinner from '../../svgs/Spinner';
import SignatureFlagName from '../../svgs/SignatureFlagName';
import SignatureFlagRole from '../../svgs/SignatureFlagRole';

import Button from '../../components/Button';
import InputGroup from '../../components/InputGroup';
import Checkbox from '../../components/Checkbox';
import CompletedSignature from '../../components/CompletedSignature';
import PatientSelector from '../../components/PatientSelector';
import { customToast } from '../../components/ToastAlert/customToast';

import Question from '../CompleteProviderForm/Question';
import { CompleteProviderFormData } from '../CompleteProviderForm/helpers';
import { coerceCheckboxBoolean } from '../../lib/register-options';
import { Transition } from '@headlessui/react';
import { defaultEnterTransitionProps } from '../../../v1/lib/animation';
import { useScrollToFirstError } from '../../hooks/useScrollToFirstError';

const CompletePractitionerForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { authedProviderUser } = useAuth();

  const assessmentId = searchParams.get('assessmentId') ?? '';
  const programIdFromUrl = searchParams.get('programId') ?? '';

  const [completeProviderOnlyForm, { loading: isCompleting }] =
    useCompleteProviderOnlyFormMutation();

  const preselectedProgramId = programIdFromUrl || '';
  const [selectedProgramId, setSelectedProgramId] =
    useState(preselectedProgramId);

  const { data: assessmentData, loading: assessmentLoading } =
    useCustomAssessmentQuery({
      variables: {
        assessmentId,
        programId: selectedProgramId || undefined,
      },
      skip: !assessmentId,
    });

  const { data: patientsData } = usePatientsV2NamesQuery({
    variables: {
      patientLifecycleStatuses: [PatientLifecycleStatusV2.Active],
      assignedPatients: true,
    },
  });

  const assessment = assessmentData?.assessment;
  const questions = (assessment?.questions ??
    []) as ProgramAssessmentQuestion[];
  const clients = patientsData?.patientNames ?? [];

  const isPractitionerForm = assessment?.completableByProviderOnly === true;
  const hasProviderSignatureQuestion = questions.some((q) =>
    isProviderSignatureAssessmentQuestion(q),
  );

  useEffect(() => {
    if (
      preselectedProgramId &&
      clients.some((c) => c.programInstanceId === preselectedProgramId)
    ) {
      setSelectedProgramId(preselectedProgramId);
    }
  }, [preselectedProgramId, clients]);

  const { register, handleSubmit, watch, setValue, control, formState, reset } =
    useForm<CompleteProviderFormData>({
      defaultValues: {
        providerSignatureName: authedProviderUser?.name ?? '',
        providerSignatureRole: '',
        signatureTermsAgreed: false,
        answers: [],
      },
    });

  useScrollToFirstError(formState);

  const watchSignatureName = watch('providerSignatureName');
  const watchSignatureRole = watch('providerSignatureRole');
  const watchSignatureTermsAgreed = watch('signatureTermsAgreed');

  useEffect(() => {
    if (authedProviderUser?.name) {
      setValue('providerSignatureName', authedProviderUser.name);
    }
  }, [authedProviderUser?.name, setValue]);

  const nowFormattedTimestamp = getTimeStamp(
    new Date().toISOString(),
    true,
    false,
  );

  const providerOnlyFormSigner = authedProviderUser
    ? {
        id: '',
        orderIndex: 0,
        providerUser: {
          id: authedProviderUser.id,
          name: authedProviderUser.name ?? '',
        },
        assessmentSignatureUser: {
          id: '',
          orderIndex: 0,
          signatureUserType: SignatureUser.SendingProviderUser,
        },
      }
    : null;

  const onSubmit: SubmitHandler<CompleteProviderFormData> = async (
    formData,
  ) => {
    const programId = selectedProgramId || preselectedProgramId;
    if (!programId) {
      customToast.error('Please select a client.');
      return;
    }
    if (!assessmentId) {
      customToast.error('Missing form.');
      return;
    }

    const answersWithProviderSignature = (formData.answers ?? []).map(
      (ans, idx) => {
        const q = questions[idx];
        if (q && isProviderSignatureAssessmentQuestion(q)) {
          return {
            ...ans,
            signatureName: formData.providerSignatureName ?? '',
          };
        }
        return ans;
      },
    );

    const answers = mapAssessmentAnswersToMutationInput(
      questions,
      answersWithProviderSignature,
    );

    try {
      await completeProviderOnlyForm({
        variables: {
          input: {
            programId,
            assessmentId,
            answers,
            signatureName: formData.providerSignatureName ?? undefined,
            signatureRole: formData.providerSignatureRole ?? undefined,
          },
        },
      });
      customToast.success(`Completed "${assessment?.name}" for client.`);
      navigate(`/clients/${programId}/documents-and-files`);
    } catch (err) {
      // An expired session token is an expected, recoverable auth outcome
      // (the practitioner's Auth0 token lapsed mid-form), not a code defect.
      // Report it as a breadcrumb instead of a Sentry exception so it doesn't
      // flood Sentry (WEB-AQ), and send the user to log back in.
      if (isExpiredTokenError(err)) {
        logger.trackEvent(
          'Practitioner form submission rejected: session expired',
          {
            programId,
            assessmentId,
          },
        );
        clearTokenPayload();
        clearAuth0AccessToken();
        Auth0Helpers.logout();
        customToast.error(
          'Your session has expired. Please log in to complete this form.',
        );
        navigate('/login');
        return;
      }

      logger.error('Error completing practitioner form', {
        programId,
        assessmentId,
        error: err,
      });
      customToast.error('Failed to complete form. Please try again.');
    }
  };

  if (!assessmentId) {
    return (
      <Transition show {...defaultEnterTransitionProps}>
        <div className="mt-20 flex w-full flex-col items-center justify-center">
          <p className="mb-4 text-body text-neutral-125">
            No form selected. Go to Library or a client’s Documents to complete
            a practitioner form.
          </p>
          <Button title="Go to Library" onClick={() => navigate('/library')} />
        </div>
      </Transition>
    );
  }

  if (assessmentLoading || !assessment) {
    return (
      <Transition show {...defaultEnterTransitionProps}>
        <div className="mt-24 flex w-full flex-row items-center justify-center">
          <Spinner />
        </div>
      </Transition>
    );
  }

  if (!isPractitionerForm) {
    return (
      <Transition show {...defaultEnterTransitionProps}>
        <div className="mt-20 flex w-full flex-col items-center justify-center">
          <p className="mb-4 text-body text-neutral-125">
            This form is not a practitioner-only form.
          </p>
          <Button title="Go back" onClick={() => navigate(-1)} />
        </div>
      </Transition>
    );
  }

  if (!authedProviderUser) {
    return (
      <Transition show {...defaultEnterTransitionProps}>
        <div className="mt-24 flex w-full flex-row items-center justify-center">
          <Spinner />
        </div>
      </Transition>
    );
  }

  return (
    <Transition show {...defaultEnterTransitionProps}>
      <div className="flex min-h-[100vh-64px] text-neutral-125">
        <div className="sticky top-16 flex h-full w-1/3 min-w-[400px] flex-col p-6 pl-20">
          <h1 className="mb-2 font-serif text-subtitle-small font-light text-green-150">
            {assessment.name}
          </h1>
          <p className="mb-4 text-caption text-neutral-200">
            {hasProviderSignatureQuestion
              ? "Complete this form to add it to the selected client's record. Your signature will be automatically added to all designated fields. You only need to sign once."
              : "Complete this form to add it to the selected client's record."}
          </p>

          <div className="mb-4 rounded-xl border border-neutral-50 p-4">
            <div className="mb-2 text-caption font-medium text-neutral-200">
              Select client
            </div>
            <PatientSelector
              value={
                (clients.find(
                  (c) => c.programInstanceId === selectedProgramId,
                ) ?? null) as unknown as Patient | null
              }
              onChange={(patient) => {
                const name = patient as unknown as PatientName | null;
                const newProgramId = name?.programInstanceId ?? '';
                setSelectedProgramId(newProgramId);
                // Reset answers so autofill fields repopulate from the new patient's data
                reset({
                  providerSignatureName: watch('providerSignatureName'),
                  providerSignatureRole: watch('providerSignatureRole'),
                  signatureTermsAgreed: watch('signatureTermsAgreed'),
                  answers: [],
                });
              }}
              placeholder="Search for a client"
              errorMessage={
                formState.submitCount > 0 && !selectedProgramId
                  ? 'Please select a client'
                  : undefined
              }
              required
            />
          </div>

          {hasProviderSignatureQuestion && (
            <div className="mt-4 rounded-xl border border-neutral-50 p-4">
              {(!watchSignatureName || !watchSignatureRole) && (
                <>
                  <div className="mb-0.5 flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-widest text-[#e08b00]">
                    ✍️ Signature required
                  </div>
                  <div className="mb-3 text-[11px] text-neutral-125">
                    Add your name and role here to sign electronically
                  </div>
                </>
              )}
              <InputGroup
                highlighted={!watchSignatureName}
                placeholder="Name"
                IconLeft={SignatureFlagName}
                containerClassName="mb-3 w-full"
                className="rounded-lg pl-12"
                {...register('providerSignatureName', {
                  required: hasProviderSignatureQuestion,
                })}
                errorMessage={formState.errors.providerSignatureName?.message}
              />
              <InputGroup
                highlighted={!watchSignatureRole}
                placeholder="Role"
                IconLeft={SignatureFlagRole}
                containerClassName="mb-3 w-full"
                className="rounded-lg pl-12"
                {...register('providerSignatureRole', {
                  required: hasProviderSignatureQuestion
                    ? { value: true, message: 'Role is required' }
                    : false,
                })}
                errorMessage={formState.errors.providerSignatureRole?.message}
              />
              {watchSignatureName && watchSignatureRole && (
                <CompletedSignature
                  signatureName={watchSignatureName}
                  signatureRole={watchSignatureRole}
                  className="mt-2 mb-3"
                />
              )}
              <Checkbox
                id="signatureTermsAgreed"
                className="mt-3"
                labelContent={
                  <div className="ml-2 text-caption">
                    I agree to the electronic signature{' '}
                    <a
                      href="https://www.homecoming.health/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-100"
                    >
                      terms of service
                    </a>
                    .
                  </div>
                }
                {...register('signatureTermsAgreed', {
                  required: hasProviderSignatureQuestion,
                  setValueAs: coerceCheckboxBoolean,
                })}
              />
            </div>
          )}

          <div className="mt-6 flex flex-row items-center justify-end">
            <Button
              title="Cancel"
              theme="secondary"
              size="small"
              className="mr-3"
              onClick={() => navigate(-1)}
            />
            <Button
              title="Complete form"
              size="small"
              onClick={handleSubmit(onSubmit)}
              disabled={
                !selectedProgramId ||
                isCompleting ||
                (hasProviderSignatureQuestion && !watchSignatureTermsAgreed)
              }
            />
          </div>
        </div>

        <div className="w-2/3 overflow-y-auto p-6">
          <div className="mx-auto max-w-[800px]">
            {assessment.questionFrame && (
              <div className="mb-4 text-caption text-neutral-125">
                {assessment.questionFrame}
              </div>
            )}
            <div className="grid gap-y-6">
              {questions.map((question, idx) => {
                const isStatement = isStatementAssessmentQuestion(question);
                const hasError = Boolean(formState.errors.answers?.[idx]);
                return (
                  <div
                    key={`${idx}-${question.question}`}
                    id={`question-${idx}`}
                    className={
                      !isStatement
                        ? `rounded-lg border p-4 ${
                            hasError ? 'border-red-100' : 'border-neutral-75'
                          }`
                        : ''
                    }
                  >
                    <Question
                      question={question}
                      answer={undefined}
                      idx={idx}
                      register={register}
                      control={control}
                      setValue={setValue}
                      programAssessmentSignatureUsers={[]}
                      currentSigner={providerOnlyFormSigner}
                      signatureName={watchSignatureName ?? ''}
                      signatureRole={watchSignatureRole ?? ''}
                      signatureTermsAgreed={watchSignatureTermsAgreed ?? false}
                      nowFormattedTimestamp={nowFormattedTimestamp}
                      formIsReadOnly={false}
                      programId={
                        selectedProgramId || preselectedProgramId || undefined
                      }
                    />
                    {hasError && (
                      <div className="mt-2 text-caption text-red-100">
                        Please answer above
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
};

export default CompletePractitionerForm;
